import * as React from "react";
import {connect} from "react-redux";
import {Option, option} from "ts-option";
import {FormBodyProps} from "../../../common/props/FormBodyProps";
import PartnerBrandForm from "../../../partnerBrand/PartnerBrandForm";
import Row from "../../../layout/Row";
import Col from "../../../layout/Col";
import CurrencyInputFormGroup from "../../../ui/form/CurrencyInputFormGroup";
import CreditValueCapFormGroup from "../CreditValueCapFormGroup";
import DealerPricesMatrix from "../../../brand/DealerPricesMatrix";
import {DealerPriceMatrixState, fromDealerPrices} from "../../../brand/dto/DealerPriceMatrixState";
import Brand from "../../../../model/brand/Brand";
import {getLoggedUser} from "../../../../store/view/user";
import {RootState} from "../../../../store/reducer/root/RootState";
import {getCurrencySymbol, getSelectedBrand} from "./PartnerBrandSections";
import {PartnerBrandSubsectionTitle} from "./PartnerBrandSectionTitle";

interface ComponentProps {
	/**
	 * On dealer price matrix change.
	 */
	onDealerPriceMatrixChange: (state: DealerPriceMatrixState) => void;
}

interface ReduxProps {
	brands: Option<Brand[]>;
}

type Props = ComponentProps & FormBodyProps<PartnerBrandForm> & ReduxProps;

/**
 * What the partner is paid, grouped by job type: the most used ones first, heat pump and Flowsave at the bottom.
 */
class PartnerBrandPayoutsSection extends React.PureComponent<Props> {
	/**
	 * If true, the Service group with the credit value cap is displayed.
	 */
	isAdmin: boolean = getLoggedUser().map(u => u.isAdmin()).getOrElse(false);

	render() {
		const form = this.props.form;
		const currencySymbol = getCurrencySymbol(form);
		const selectedBrand = getSelectedBrand(form, this.props.brands);
		return (
			<>
				<PartnerBrandSubsectionTitle title={"Registration"} first={true}/>
				<DealerPricesMatrix
					state={form.dealerPriceMatrix}
					onChange={this.props.onDealerPriceMatrixChange}
					currencySymbol={currencySymbol}
					inheritedState={selectedBrand.map(brand => fromDealerPrices(brand.dealerPrices)).orNull || undefined}
					error={form.getDealerPriceMatrixError()}/>
				{this.isAdmin && <>
				<PartnerBrandSubsectionTitle title={"Service"}/>
				<Row>
					<Col lg={6} xl={6}>
						{/* The inherited cap comes from the parent Brand already in the store, the same place the
						inherited dealer prices come from. flatMap, so that a brand served without the amount reads
						as "not known" instead of turning into "Currently €NaN" on the inherit option. */}
						<CreditValueCapFormGroup label={`Credit value cap (${currencySymbol})`}
												 modeField={form.creditValueCapMode}
												 amountField={form.creditValueCap}
												 inheritedCapCents={selectedBrand.flatMap(brand => option(brand.creditValueCap))}
												 brandName={selectedBrand.map(brand => brand.name)}
												 currencySymbol={currencySymbol}
												 error={form.getCreditValueCapError()}
												 onChange={this.props.onFieldChange}/>
					</Col>
				</Row>
				</>}
				<PartnerBrandSubsectionTitle title={"Heat pump"}/>
				<Row>
					<Col lg={4} xl={4}>
						<CurrencyInputFormGroup field={form.creditValueAirToWaterHp}
												placeholder={"Air to Water"}
												onChange={this.props.onFieldChange}
												label={`Air to Water (${currencySymbol})`}
												focus={form.hasFocus(form.creditValueAirToWaterHp)}/>
					</Col>
					<Col lg={4} xl={4}>
						<CurrencyInputFormGroup field={form.creditValueAirToAirHp}
												placeholder={"Air to Air"}
												onChange={this.props.onFieldChange}
												label={`Air to Air (${currencySymbol})`}
												focus={form.hasFocus(form.creditValueAirToAirHp)}/>
					</Col>
					<Col lg={4} xl={4}>
						<CurrencyInputFormGroup field={form.creditValueWaterToWaterHp}
												placeholder={"Water to Water"}
												onChange={this.props.onFieldChange}
												label={`Water to Water (${currencySymbol})`}
												focus={form.hasFocus(form.creditValueWaterToWaterHp)}/>
					</Col>
					<Col lg={4} xl={4}>
						<CurrencyInputFormGroup field={form.creditValueBrineToWaterHp}
												placeholder={"Brine to Water"}
												onChange={this.props.onFieldChange}
												label={`Brine to Water (${currencySymbol})`}
												focus={form.hasFocus(form.creditValueBrineToWaterHp)}/>
					</Col>
				</Row>
				<PartnerBrandSubsectionTitle title={"Flowsave"}/>
				<Row>
					<Col lg={4} xl={4}>
						<CurrencyInputFormGroup field={form.creditValueFlowsave}
												placeholder={"Flowsave"}
												onChange={this.props.onFieldChange}
												label={`Flowsave (${currencySymbol})`}
												focus={form.hasFocus(form.creditValueFlowsave)}/>
					</Col>
				</Row>
			</>
		);
	}
}

function mapStateToProps(state: RootState): ReduxProps {
	return {
		brands: state.brand.brands
	};
}

export default connect(mapStateToProps)(PartnerBrandPayoutsSection);
